/**
 * Soft-safe **생존 추구 (survival seeking)** — Option A.
 * 직감 = when endangered, open seat-break tip (≤20 → 0).
 * 통찰 = among Soft≪AA-clear seats prefer survival-favorable:
 *   healer staff / predicted approach · behind soak/tank · avoid chew face.
 * Adjutant never relocates — optional seed is score hint only.
 * Claim B banned · Soft clear dominates · do NOT wire instinctAllowsCoverSeek alone.
 */
import { instinctAllowsSeatBreak } from "./polar";
import { judgmentNorm } from "./traits";
import { dist2d } from "./cover-subject";
import type { RaidUnit } from "../raid-types";
import { getJudgmentAssistKnobs } from "./knobs";
import { getSoftAaIsolateFlags } from "./soft-aa-isolate";
import {
  rolePolicyTable,
  unitHasRoleTag,
  unitIsStaffHealer,
} from "./hero-registry";
import { scoreJointSeat } from "./joint-seat";

export type SurvivalApproachHint = {
  /** Healer / ally unit id for seed lookup. */
  unitId: number;
  /** Predicted approach (backline seed hint); null = current feet only. */
  seed: { x: number; y: number } | null;
};

export type SurvivalThreatHint = {
  x: number;
  y: number;
};

export type SurvivalSeekSeatArgs = {
  x: number;
  y: number;
  selfId?: number;
  allies: readonly RaidUnit[];
  insight: number;
  instinct: number;
  personalClear01?: number;
  /** Pack / threat centroid for soak-line + chew-face terms. */
  fleeFrom?: SurvivalThreatHint | null;
  approachHints?: readonly SurvivalApproachHint[];
};

/** Soft ship / playable: ON when Soft flag ∧ rolePolicy allow. */
export function survivalSeekScorerEnabled(): boolean {
  if (!getSoftAaIsolateFlags().survivalSeekScorer) return false;
  return rolePolicyTable().survivalSeekEnabled;
}

export function survivalSeekSeatFilterScore(): number {
  const k = getJudgmentAssistKnobs();
  if (k.seatFilterScore > 0) return k.seatFilterScore;
  return rolePolicyTable().survivalSeekSeatFilterScore;
}

export function survivalSeekPredictBlendMax(): number {
  const k = getJudgmentAssistKnobs();
  if (k.predictBlendMax != null && k.predictBlendMax >= 0) {
    return k.predictBlendMax;
  }
  return rolePolicyTable().survivalSeekPredictBlendMax;
}

/** Allies who can soak / stand in front (tank or frontline). */
export function livingSoakAllies(
  allies: readonly RaidUnit[],
  excludeId?: number,
): RaidUnit[] {
  return allies.filter(
    (a) =>
      a.hp > 0 &&
      (excludeId == null || a.id !== excludeId) &&
      (unitHasRoleTag(a, "tank") || unitHasRoleTag(a, "frontline")),
  );
}

/**
 * Blend healer current feet toward backline seed by insight.
 * insight 0 → current · insight 100 → up to predictBlendMax toward seed.
 */
export function predictedHealerApproach(
  healer: Pick<RaidUnit, "x" | "y">,
  backlineSeed: { x: number; y: number } | null,
  insight: number,
  predictBlendMax = survivalSeekPredictBlendMax(),
): { x: number; y: number } {
  if (!backlineSeed) return { x: healer.x, y: healer.y };
  const t = Math.max(0, Math.min(1, insight / 100));
  const blend = Math.min(predictBlendMax, t * predictBlendMax);
  return {
    x: healer.x + (backlineSeed.x - healer.x) * blend,
    y: healer.y + (backlineSeed.y - healer.y) * blend,
  };
}

/**
 * 0..1 — seat sits behind a soak ally relative to threat (soak between threat & seat).
 */
export function behindSoakScore01(
  x: number,
  y: number,
  soaks: readonly RaidUnit[],
  threat: SurvivalThreatHint | null,
): number {
  if (!threat || soaks.length === 0) return 0;
  let best = 0;
  for (const s of soaks) {
    const sx = s.x - threat.x;
    const sy = s.y - threat.y;
    const hx = x - threat.x;
    const hy = y - threat.y;
    const sLen = Math.hypot(sx, sy) || 1;
    const hLen = Math.hypot(hx, hy) || 1;
    if (hLen < sLen + 6) continue;
    const align = (sx * hx + sy * hy) / (sLen * hLen);
    if (align < 0.5) continue;
    const cross = Math.abs(sx * hy - sy * hx) / sLen;
    const lat01 = Math.max(0, 1 - cross / 90);
    const depth01 = Math.min(1, (hLen - sLen) / 110);
    best = Math.max(best, lat01 * 0.55 + depth01 * 0.45);
  }
  return best;
}

/**
 * 0..1 risk — candidate is closer to threat than living soaks (chew face).
 */
export function chewFaceRisk01(
  x: number,
  y: number,
  soaks: readonly RaidUnit[],
  threat: SurvivalThreatHint | null,
): number {
  if (!threat || soaks.length === 0) return 0;
  const myD = dist2d(x, y, threat.x, threat.y);
  let nearestSoak = Infinity;
  for (const s of soaks) {
    nearestSoak = Math.min(nearestSoak, dist2d(s.x, s.y, threat.x, threat.y));
  }
  if (!(nearestSoak < Infinity)) return 0;
  if (myD >= nearestSoak - 4) return 0;
  return Math.max(0, Math.min(1, (nearestSoak - myD) / 80));
}

const STAFF_W = 0.45;
const SOAK_W = 0.4;
const CHEW_PEN = 0.3;

/**
 * Soft-safe additive tip among already-clear seats (motion entry).
 * Returns 0 when instinct≤20, scorer off, or seat not clear.
 * Soft≪AA clear terms must dominate at the call site.
 */
export function survivalSeekSeatBonus(args: SurvivalSeekSeatArgs): number {
  const personalClear01 = args.personalClear01 ?? 1;
  if (!survivalSeekScorerEnabled()) return 0;
  if (!instinctAllowsSeatBreak(args.instinct)) return 0;
  if (personalClear01 < 1) return 0;
  const filterScore = survivalSeekSeatFilterScore();
  if (filterScore <= 0) return 0;
  const k = getJudgmentAssistKnobs();
  if (args.insight < k.jointInsightMin) return 0;

  const selfId = args.selfId;
  const healers = args.allies.filter((a) => unitIsStaffHealer(a));
  const soaks = livingSoakAllies(args.allies, selfId);
  const threat = args.fleeFrom ?? null;
  const hints = args.approachHints ?? [];

  const staff = scoreJointSeat(
    args.x,
    args.y,
    healers,
    args.insight,
    personalClear01,
  );
  let approach01 = 0;
  for (const h of healers) {
    const hint = hints.find((a) => a.unitId === h.id);
    const approach = predictedHealerApproach(
      h,
      hint?.seed ?? null,
      args.insight,
    );
    const d = dist2d(args.x, args.y, approach.x, approach.y);
    const near = Math.max(0, 1 - d / 220);
    if (near > approach01) approach01 = near;
  }
  const soak01 = behindSoakScore01(args.x, args.y, soaks, threat);
  const chewRisk = chewFaceRisk01(args.x, args.y, soaks, threat);
  const insightT = Math.max(0, judgmentNorm(args.insight));

  const staffTerm =
    staff.score01 * (0.5 + 0.2 * insightT) +
    approach01 * (0.2 + 0.2 * insightT);
  const soakTerm = soak01 * (0.45 + 0.25 * insightT);
  const tip01 = Math.max(
    0,
    Math.min(1, staffTerm * STAFF_W + soakTerm * SOAK_W - chewRisk * CHEW_PEN),
  );
  return tip01 * filterScore;
}

/** @deprecated Prefer {@link survivalSeekSeatBonus}. */
export function survivalSeekScoreBonus(
  x: number,
  y: number,
  allies: readonly RaidUnit[],
  insight: number,
  instinct: number,
  personalClear01 = 1,
  opts?: {
    selfId?: number;
    threat?: SurvivalThreatHint | null;
    approachHints?: readonly SurvivalApproachHint[];
  },
): number {
  return survivalSeekSeatBonus({
    x,
    y,
    allies,
    insight,
    instinct,
    personalClear01,
    selfId: opts?.selfId,
    fleeFrom: opts?.threat ?? null,
    approachHints: opts?.approachHints,
  });
}
